import { computed, ref, watch, type Component } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'

import { SEARCH_DEBOUNCE_MS } from '@/shared/constants'
import { Aim, Location, RefreshRight, Suitcase } from '@/shared/icons'
import {
  SearchResultKind,
  useSearchResults,
  type SearchResult
} from '@/apps/adok/entities/search'

/**
 * Below this the answer is most of what the publisher owns, which is no answer at all.
 */
const MIN_QUERY_LENGTH = 3

const RESULT_LIMIT = 20

const DESTINATIONS: Record<SearchResultKind, (id: string) => RouteLocationRaw> =
  {
    [SearchResultKind.Website]: (id) => ({
      name: 'websites-edit',
      params: { websiteId: id }
    }),
    [SearchResultKind.Spot]: (id) => ({
      name: 'spots-edit',
      params: { spotId: id }
    }),
    [SearchResultKind.Creative]: (id) => ({
      name: 'creatives-edit',
      params: { id }
    }),
    [SearchResultKind.FlatDeal]: (id) => ({
      name: 'deals-edit',
      params: { dealId: id }
    })
  }

const ICONS: Record<SearchResultKind, Component> = {
  [SearchResultKind.Website]: Location,
  [SearchResultKind.Spot]: Aim,
  [SearchResultKind.Creative]: RefreshRight,
  [SearchResultKind.FlatDeal]: Suitcase
}

/** Two kinds can carry the same number, so an option value has to say which it is. */
const valueOf = (item: SearchResult) => `${item.kind}:${item.id}`

const labelOf = (item: SearchResult) =>
  item.kind === SearchResultKind.Spot
    ? `${item.id}. ${item.name} at (${item.platformName})`
    : `${item.id}. ${item.name}`

export function useEntitySearch() {
  const router = useRouter()
  const route = useRoute()

  const selected = ref<string | null>(null)
  const query = ref('')

  const onSearch = useDebounceFn((value: string) => {
    query.value = value
  }, SEARCH_DEBOUNCE_MS)

  const isAsking = computed(() => query.value.length >= MIN_QUERY_LENGTH)

  const { items, isFetching } = useSearchResults(
    () => ({ query: query.value, limit: RESULT_LIMIT }),
    { enabled: isAsking }
  )

  // Gated on the question as well as the answer: the query keeps its previous rows as
  // placeholder data even while disabled, so an emptied box would still list them.
  const options = computed(() =>
    isAsking.value
      ? items.value.map((item) => ({
          label: labelOf(item),
          value: valueOf(item),
          icon: ICONS[item.kind]
        }))
      : []
  )

  // `Select` announces a change with the whole of what a select can hold. This one is
  // neither `multiple` nor numeric, so a string is the only shape that can name a row.
  function onSelect(value: string | number | (string | number)[] | null) {
    const item =
      typeof value === 'string'
        ? items.value.find((row) => valueOf(row) === value)
        : undefined

    if (item) router.push(DESTINATIONS[item.kind](item.id))
  }

  // The box holds no value of its own: it asks a question, hands the answer to the router
  // and empties itself once the publisher has arrived.
  watch(
    () => route.fullPath,
    () => {
      selected.value = null
      query.value = ''
    }
  )

  return { selected, options, isFetching, onSearch, onSelect }
}
